import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { LOCATION_PAGES_DATA } from "../data/locationPagesData";
import SEO from "../Components/SEO";
import "./LocationPage.css";
import {
  FaCheckCircle,
  FaStar,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaWhatsapp,
  FaEnvelope,
  FaHome,
  FaBuilding,
  FaTools,
  FaShieldAlt,
  FaDraftingCompass,
  FaMoneyBillWave,
  FaChevronDown,
  FaArrowRight
} from "react-icons/fa";

export default function LocationPage({ locationKey }) {
  const { citySlug } = useParams();
  const activeKey = (locationKey || citySlug || "usilampatti").toLowerCase();
  const cityData = LOCATION_PAGES_DATA[activeKey] || LOCATION_PAGES_DATA["usilampatti"];

  const [activeFaq, setActiveFaq] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    serviceType: "Residential Construction",
    budget: "₹15 Lakhs - ₹30 Lakhs",
    message: ""
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveFaq(null);
    setFormSubmitted(false);
  }, [activeKey]);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  // List of other regions for cross-linking & SEO authority
  const otherLocations = Object.values(LOCATION_PAGES_DATA).filter(
    (loc) => loc.slug !== cityData.slug
  );

  return (
    <div className="location-page-wrapper">
      <SEO
        title={cityData.metaTitle}
        description={cityData.metaDescription}
        keywords={`${cityData.metaKeywords}, BEST CONSTRUCTION IN MADURAI, TOP BUILDERS IN MADURAI, RESIDENTIAL CONSTRUCTION IN MADURAI, HOME REPAIR IN MADURAI, BUDGET FRIENDLY HOUSE CONSTRUCTION IN MADURAI`}
        canonicalUrl={`https://varkbuilders.com/${cityData.slug}`}
        imageUrl={`https://varkbuilders.com/newlogo.png`}
        ratingValue={cityData.rating || "4.9"}
        reviewCount={cityData.reviewCount || 420}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Service Areas", url: "/#service-areas" },
          { name: cityData.cityName, url: `/${cityData.slug}` }
        ]}
      />

      {/* 1. LOCALIZED HERO BANNER */}
      <section
        className="location-hero"
        style={{
          backgroundImage: `linear-gradient(rgba(10, 15, 25, 0.78), rgba(10, 15, 25, 0.88)), url(${cityData.heroImage})`
        }}
      >
        <div className="container">
          <div className="location-hero-content">
            {/* Top Badges */}
            <div className="location-badges-row">
              <span className="location-district-badge">
                <FaMapMarkerAlt /> {cityData.district}
              </span>
              <div className="location-star-rating-badge">
                <div className="stars-group">
                  <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
                </div>
                <strong>{cityData.rating} / 5.0</strong>
                <span className="review-pill">({cityData.reviewCount}+ Google Reviews)</span>
              </div>
            </div>

            <h1 className="location-hero-title">{cityData.heroHeading}</h1>
            <p className="location-hero-subtitle">{cityData.heroSubheading}</p>

            {/* Quick Stats Pill */}
            <div className="location-hq-reach">
              <span className="pulse-dot"></span>
              <strong>Fast Response:</strong> {cityData.distanceFromHQ}
            </div>

            {/* CTA Buttons */}
            <div className="location-hero-cta-group">
              <a href="#quick-quote-form" className="location-primary-btn">
                Get Free Estimation in {cityData.cityName}
              </a>
              <a
                href="tel:+919842943377"
                className="location-call-btn"
                aria-label="Call VARK Builders Engineer"
              >
                <FaPhoneAlt /> +91 98429 43377
              </a>
              <a
                href={`https://wa.me/919842943377?text=Hi%20VARK%20Builders,%20I%20am%20looking%20for%20construction%20in%20${encodeURIComponent(
                  cityData.cityName
                )}`}
                target="_blank"
                rel="noopener noreferrer"
                className="location-wa-btn"
                aria-label="WhatsApp VARK Builders"
              >
                <FaWhatsapp /> WhatsApp Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. LOCAL AREA COVERAGE HIGHLIGHTS */}
      <section className="location-coverage-section">
        <div className="container">
          <div className="coverage-card">
            <h3>
              <FaMapMarkerAlt className="icon-gold" /> Key Neighborhoods Covered Across {cityData.cityName}:
            </h3>
            <p className="coverage-sub">
              Our civil engineering teams provide on-site supervision, daily updates, and rapid material delivery across:
            </p>
            <div className="coverage-tags-grid">
              {cityData.popularAreas.map((area, idx) => (
                <span key={idx} className="coverage-tag">
                  ✓ {area}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 4. BUDGET-FRIENDLY HOUSE CONSTRUCTION PACKAGES */}
      <section className="location-packages-section">
        <div className="container">
          <div className="section-head text-center">
            <span className="section-tag">BUDGET-FRIENDLY PACKAGES</span>
            <h2>Transparent House Construction Rates in {cityData.cityName}</h2>
            <p>
              Best Construction in {cityData.cityName} with zero hidden costs, 100% branded materials, and on-time structural delivery.
            </p>
          </div>

          <div className="packages-grid">
            {cityData.budgetPackages.map((pkg, idx) => (
              <div key={idx} className={`package-card ${idx === 1 ? "featured-package" : ""}`}>
                {idx === 1 && <span className="package-ribbon">Most Popular</span>}
                <div className="package-header">
                  <h3 className="package-title">{pkg.name}</h3>
                  <div className="package-rate">{pkg.rate}</div>
                </div>
                <p className="package-desc">{pkg.desc}</p>
                <ul className="package-checklist">
                  <li><FaCheckCircle className="check-icon" /> 100% Vastu Compliant Design</li>
                  <li><FaCheckCircle className="check-icon" /> Branded TMT Steel & 53-Grade Cement</li>
                  <li><FaCheckCircle className="check-icon" /> Multi-Layer Waterproofing Warranty</li>
                  <li><FaCheckCircle className="check-icon" /> 2D Floor Plan & 3D Architectural Elevation</li>
                  <li><FaCheckCircle className="check-icon" /> Turnkey Material & Labor Contract</li>
                </ul>
                <a href="#quick-quote-form" className="package-btn">
                  Choose {pkg.name}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. CORE CIVIL & CONTRACTING SERVICES IN REGION */}
      <section className="location-services-section">
        <div className="container">
          <div className="section-head text-center">
            <span className="section-tag">COMPREHENSIVE SERVICES</span>
            <h2>Our Building Capabilities in {cityData.cityName}</h2>
            <p>From individual budget houses to luxury mansions and turnkey civil projects.</p>
          </div>

          <div className="loc-services-grid">
            <div className="loc-service-card">
              <div className="icon-wrapper"><FaHome /></div>
              <h3>Residential Construction</h3>
              <p>Individual villas, duplex houses, and multi-family homes designed for natural ventilation and thermal comfort.</p>
            </div>

            <div className="loc-service-card">
              <div className="icon-wrapper"><FaTools /></div>
              <h3>Home Repair & Renovation</h3>
              <p>Expert structural rehabilitation, crack repair, terrace waterproofing, tile re-laying, and modern bathroom upgrades in {cityData.cityName}.</p>
            </div>

            <div className="loc-service-card">
              <div className="icon-wrapper"><FaBuilding /></div>
              <h3>Commercial Construction</h3>
              <p>Showrooms, retail shops, corporate offices, and educational infrastructure engineered for commercial durability.</p>
            </div>

            <div className="loc-service-card">
              <div className="icon-wrapper"><FaDraftingCompass /></div>
              <h3>3D Elevation & Planning</h3>
              <p>Modern contemporary, traditional Tamil heritage, and Mediterranean 3D elevation designs tailored to your plot dimensions.</p>
            </div>

            <div className="loc-service-card">
              <div className="icon-wrapper"><FaShieldAlt /></div>
              <h3>Turnkey Approvals & Approvals</h3>
              <p>DTCP, Panchayat, and Municipal plan sanctions, soil testing, structural stability certificates, and hassle-free handovers.</p>
            </div>

            <div className="loc-service-card">
              <div className="icon-wrapper"><FaMoneyBillWave /></div>
              <h3>Budget-Friendly Housing</h3>
              <p>Optimized structural engineering providing maximum carpet area and durability at the most competitive price per square foot.</p>
            </div>
          </div>
        </div>
      </section>

      {/* 6. ONGOING & COMPLETED PROJECTS IN THIS AREA */}
      <section className="location-projects-section">
        <div className="container">
          <div className="section-head text-center">
            <span className="section-tag">LOCAL DEVELOPMENTS</span>
            <h2>Featured Projects in {cityData.cityName}</h2>
            <p>Take a look at our current and recently delivered residences in {cityData.cityName}.</p>
          </div>

          <div className="loc-projects-grid">
            {cityData.projectsInArea.map((project, idx) => (
              <div key={idx} className="loc-project-card">
                <div className="project-image-box">
                  <img
                    src={project.img}
                    alt={`${project.name} - ${project.type} in ${project.location}`}
                    loading="lazy"
                  />
                  <span className="project-status-badge">{project.status}</span>
                </div>
                <div className="project-info-box">
                  <span className="project-type">{project.type}</span>
                  <h3 className="project-title">{project.name}</h3>
                  <p className="project-location">
                    <FaMapMarkerAlt /> {project.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. STAR RATING & CLIENT TRUST SECTION */}
      <section className="location-reviews-section">
        <div className="container">
          <div className="reviews-summary-card">
            <div className="rating-left">
              <span className="rating-number">{cityData.rating}</span>
              <div className="stars-row">
                <FaStar /><FaStar /><FaStar /><FaStar /><FaStar />
              </div>
              <span className="rating-desc">
                Google Verified Rating ({cityData.reviewCount}+ Homeowner Reviews)
              </span>
            </div>
            <div className="rating-right">
              <h3>Why Families in {cityData.cityName} Choose VARK Builders</h3>
              <div className="features-inline">
                <span>✓ On-Time Handover Guarantee</span>
                <span>✓ Fixed Price - No Cost Escalation</span>
                <span>✓ Daily WhatsApp Site Photo Updates</span>
                <span>✓ 10-Year Structural Warranty</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 8. LOCALIZED FAQ SECTION */}
      <section className="location-faq-section">
        <div className="container">
          <div className="section-head text-center">
            <span className="section-tag">FREQUENTLY ASKED QUESTIONS</span>
            <h2>House Construction FAQs for {cityData.cityName}</h2>
            <p>Everything you need to know about building your dream home with VARK Builders.</p>
          </div>

          <div className="faq-accordion-container">
            {cityData.faqs.map((faq, idx) => {
              const isOpen = activeFaq === idx;
              return (
                <div key={idx} className={`faq-card ${isOpen ? "faq-open" : ""}`}>
                  <button
                    className="faq-question-btn"
                    onClick={() => toggleFaq(idx)}
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <FaChevronDown className={`chevron-icon ${isOpen ? "rotate" : ""}`} />
                  </button>
                  {isOpen && (
                    <div className="faq-answer-panel">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 9. FAST ENQUIRY FORM */}
      <section id="quick-quote-form" className="location-enquiry-section">
        <div className="container">
          <div className="enquiry-card-grid">
            <div className="enquiry-text-column">
              <span className="section-tag">GET A FAST QUOTE</span>
              <h2>Start Building Your Dream Home in {cityData.cityName}</h2>
              <p>
                Schedule a complimentary site visit with our senior structural engineer. Receive a customized 2D layout plan and accurate quotation within 24 hours.
              </p>

              <div className="contact-methods">
                <div className="method-item">
                  <FaPhoneAlt className="method-icon" />
                  <div>
                    <strong>Direct Engineer Consultation:</strong>
                    <p>+91 98429 43377</p>
                  </div>
                </div>
                <div className="method-item">
                  <FaEnvelope className="method-icon" />
                  <div>
                    <strong>Email Enquiries:</strong>
                    <p>varkbuilders@gmail.com</p>
                  </div>
                </div>
                <div className="method-item">
                  <FaMapMarkerAlt className="method-icon" />
                  <div>
                    <strong>Corporate Head Office:</strong>
                    <p>20, 1st Floor, Annai Teresa street, Bypass Rd, Fathima Nagar, Madurai - 625016</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="enquiry-form-column">
              <form onSubmit={handleSubmit} className="loc-quick-form">
                <h3>Request Free Site Estimate</h3>
                {formSubmitted && (
                  <div className="form-success-banner">
                    🎉 Thank you! Our {cityData.cityName} site engineer will contact you shortly.
                  </div>
                )}

                <div className="form-group">
                  <label>Your Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your name"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Mobile Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="10-digit mobile number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="name@example.com"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Service Needed</label>
                    <select
                      name="serviceType"
                      value={formData.serviceType}
                      onChange={handleInputChange}
                    >
                      <option value="Residential Construction">Residential House Construction</option>
                      <option value="Home Repair & Renovation">Home Repair & Renovation</option>
                      <option value="Commercial Building">Commercial Building</option>
                      <option value="Turnkey Villa">Turnkey Luxury Villa</option>
                      <option value="Architectural Planning">Architectural 3D Plan</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Budget Estimate</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                    >
                      <option value="<15 Lakhs">Below ₹15 Lakhs</option>
                      <option value="₹15 Lakhs - ₹30 Lakhs">₹15 Lakhs - ₹30 Lakhs</option>
                      <option value="₹30 Lakhs - ₹60 Lakhs">₹30 Lakhs - ₹60 Lakhs</option>
                      <option value="₹60 Lakhs - ₹1.5 Cr">₹60 Lakhs - ₹1.5 Crore</option>
                      <option value=">1.5 Cr">Above ₹1.5 Crore</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Project Details / Plot Location in {cityData.cityName}</label>
                  <textarea
                    name="message"
                    rows="3"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={`e.g. Plot size, landmark in ${cityData.cityName}, expected handover date...`}
                  ></textarea>
                </div>

                <button type="submit" className="form-submit-btn">
                  Submit Estimate Request &rarr;
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* 10. SUGGESTION PAGES / CROSS-LINKING HUBS */}
      <section className="location-suggestions-section">
        <div className="container">
          <div className="section-head text-center">
            <span className="section-tag">EXPLORE REGIONAL HUBS</span>
            <h2>Other Construction Service Locations in Tamil Nadu</h2>
            <p>VARK Builders provides structural excellence across major southern districts:</p>
          </div>

          <div className="suggestions-grid">
            {otherLocations.map((loc) => (
              <Link to={`/${loc.slug}`} key={loc.slug} className="suggestion-card">
                <div className="sugg-header">
                  <span className="sugg-pin">📍</span>
                  <div className="sugg-title-box">
                    <h4>{loc.cityName}</h4>
                    <span className="sugg-district">{loc.district}</span>
                  </div>
                </div>
                <p className="sugg-desc">
                  Budget-friendly houses, luxury villas, and turnkey construction starting from ₹1,850/sq.ft.
                </p>
                <div className="sugg-meta">
                  <span className="sugg-rating">⭐ {loc.rating}/5.0</span>
                  <span className="sugg-link">
                    Explore {loc.cityName} <FaArrowRight />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
